import { useState, useRef, useCallback, useEffect } from "react";
import { Phone, PhoneOff, Mic, MicOff, Volume2, Loader2 } from "lucide-react";
import "./LiveCallPlugin.css";

/* ── Types ── */
type CallState = "idle" | "connecting" | "active" | "ending";

interface LiveCallPluginProps {
  /** WebSocket endpoint for the live call gateway */
  wsUrl?: string;
}

/* ── Constants ── */
const OUTPUT_SAMPLE_RATE = 24000;
const BARGE_IN_THRESHOLD = 0.08; // RMS amplitude threshold for interrupting AI speech

export default function LiveCallPlugin({
  wsUrl = `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}/ws/live-call`,
}: LiveCallPluginProps) {
  const [callState, setCallState] = useState<CallState>("idle");
  const [micMuted, setMicMuted] = useState(false);
  const [statusText, setStatusText] = useState("");

  const wsRef = useRef<WebSocket | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const wakeLockRef = useRef<any>(null);
  const playbackQueueRef = useRef<AudioBufferSourceNode[]>([]);
  const nextPlaybackTimeRef = useRef<number>(0);
  const gainNodeRef = useRef<GainNode | null>(null);
  const micMutedRef = useRef(false);

  // Keep muted ref in sync
  useEffect(() => {
    micMutedRef.current = micMuted;
  }, [micMuted]);

  /* ── AudioContext init (iOS unlock) ── */
  const ensureAudioContext = useCallback(async () => {
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
      audioCtxRef.current = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE });
    }
    if (audioCtxRef.current.state === "suspended") {
      await audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  /* ── Cleanup ── */
  const endCall = useCallback(async () => {
    setCallState("ending");

    // Stop mic
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (workletNodeRef.current) {
      workletNodeRef.current.port.postMessage({ type: "stop" });
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current) {
      try { wsRef.current.close(1000, "user_end"); } catch { /* ok */ }
      wsRef.current = null;
    }

    // Release WakeLock
    if (wakeLockRef.current) {
      try { await wakeLockRef.current.release(); } catch { /* ok */ }
      wakeLockRef.current = null;
    }

    // Close AudioContext
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      await audioCtxRef.current.close().catch(() => {});
    }
    audioCtxRef.current = null;
    gainNodeRef.current = null;
    playbackQueueRef.current = [];
    nextPlaybackTimeRef.current = 0;

    setCallState("idle");
    // Don't clear statusText — preserve error messages for the user to see
    // setStatusText is only cleared when a new Start is clicked
    setMicMuted(false);
  }, []);

  /* ── Barge-in: flush output playback ── */
  const flushPlayback = useCallback(() => {
    for (const node of playbackQueueRef.current) {
      try { node.stop(); } catch { /* already stopped */ }
    }
    playbackQueueRef.current = [];
    nextPlaybackTimeRef.current = 0;
  }, []);

  /* ── Schedule output PCM for playback ── */
  const scheduleOutputAudio = useCallback(
    (pcmBuffer: ArrayBuffer) => {
      const ctx = audioCtxRef.current;
      const gain = gainNodeRef.current;
      if (!ctx || !gain || ctx.state === "closed") return;

      const pcm = new Int16Array(pcmBuffer);
      const float32 = new Float32Array(pcm.length);
      for (let i = 0; i < pcm.length; i++) {
        float32[i] = pcm[i]! / 32768;
      }

      const ab = ctx.createBuffer(1, float32.length, OUTPUT_SAMPLE_RATE);
      if (!ab) return;
      const ch = ab.getChannelData(0);
      if (!ch) return;
      ch.set(float32);

      const source = ctx.createBufferSource();
      source.buffer = ab;
      source.connect(gain);

      const now = ctx.currentTime;
      const startTime = Math.max(now, nextPlaybackTimeRef.current);
      source.start(startTime);
      nextPlaybackTimeRef.current = startTime + ab.duration;

      playbackQueueRef.current.push(source);
      source.onended = () => {
        const idx = playbackQueueRef.current.indexOf(source);
        if (idx !== -1) playbackQueueRef.current.splice(idx, 1);
      };
    },
    []
  );

  /* ── Start Call ── */
  const startCall = useCallback(async () => {
    if (callState !== "idle") return;
    setCallState("connecting");
    setStatusText("Requesting microphone…");

    try {
      // 1. Audio unlock (must be in user gesture context)
      const ctx = await ensureAudioContext();
      gainNodeRef.current = ctx.createGain();
      gainNodeRef.current.gain.value = 1.0;
      gainNodeRef.current.connect(ctx.destination);

      // 2. Load AudioWorklet
      try {
        await ctx.audioWorklet.addModule("/pcm-processor.js");
      } catch (err) {
        // Already loaded — ignore
        if (!(err instanceof DOMException && err.message.includes("already"))) throw err;
      }

      // 3. Get microphone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
      micStreamRef.current = stream;

      // 4. Create worklet node for resampling
      const workletNode = new AudioWorkletNode(ctx, "pcm-processor", {
        processorOptions: {},
      });
      workletNodeRef.current = workletNode;

      // Tell worklet the input sample rate
      const inputRate = ctx.sampleRate;
      workletNode.port.postMessage({ type: "setSampleRate", sampleRate: inputRate });

      // 5. Connect mic → worklet (no output to speakers — avoid feedback)
      const source = ctx.createMediaStreamSource(stream);
      source.connect(workletNode);
      // workletNode is NOT connected to destination — PCM goes to WebSocket only

      // 6. Screen Wake Lock
      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      } catch {
        // Not supported — fine
      }

      // 7. Open WebSocket to backend
      setStatusText("Connecting to AI…");
      console.log("[LiveCall] Connecting to:", wsUrl);
      const ws = new WebSocket(wsUrl);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      // Connection timeout (10s)
      const connectTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.warn("[LiveCall] WebSocket connection timed out");
          setStatusText("Connection timed out — is the backend running?");
          ws.close();
        }
      }, 10000);

      ws.onopen = () => {
        clearTimeout(connectTimeout);
        console.log("[LiveCall] WebSocket connected");
        setCallState("active");
        setStatusText("Connected");
      };

      ws.onmessage = (event) => {
        if (typeof event.data === "string") {
          // JSON control messages
          try {
            const msg = JSON.parse(event.data);
            switch (msg.type) {
              case "ready":
                setStatusText(msg.model ? `AI ready (${msg.model})` : "AI ready");
                break;
              case "interrupted":
                flushPlayback();
                break;
              case "transcript":
                // Could show captions here — minimal for now
                break;
              case "error":
                setStatusText(msg.message || "Error");
                break;
              case "closed":
                console.warn("[LiveCall] Server closed:", msg.reason);
                setStatusText(msg.reason || "Call ended by server");
                endCall();
                break;
            }
          } catch { /* ignore parse errors */ }
        } else if (event.data instanceof ArrayBuffer) {
          // Binary PCM audio from AI (24kHz Int16)
          scheduleOutputAudio(event.data);
        }
      };

      ws.onerror = (ev) => {
        console.error("[LiveCall] WebSocket error:", ev);
        setStatusText("Connection failed — check backend");
        endCall();
      };

      ws.onclose = (ev) => {
        console.log("[LiveCall] WebSocket closed:", ev.code, ev.reason);
        // Don't overwrite a specific error with a generic disconnect message
        setStatusText((prev) => prev || (ev.code === 1006 ? "Backend unreachable" : "Disconnected"));
        endCall();
      };

      // 8. Forward PCM chunks from worklet → WebSocket
      workletNode.port.onmessage = (e: MessageEvent) => {
        const d = e.data as { type?: string; buffer?: ArrayBuffer } | undefined;
        if (!d || d.type !== "pcm" || !(d.buffer instanceof ArrayBuffer)) return;
        const buf: ArrayBuffer = d.buffer;
        if (ws.readyState === WebSocket.OPEN && !micMutedRef.current) {
          // Check barge-in: compute RMS to detect user speech while AI is talking
          const pcm = new Int16Array(buf);
          let sumSq = 0;
          const len = pcm.length;
          for (let i = 0; i < len; i++) {
            sumSq += (pcm[i]! / 32768) ** 2;
          }
          const rms = Math.sqrt(sumSq / len);

          // If user is speaking loudly, send barge-in signal first
          if (rms > BARGE_IN_THRESHOLD && playbackQueueRef.current.length > 0) {
            ws.send(JSON.stringify({ type: "interrupt" }));
            flushPlayback();
          }

          ws.send(buf);
        }
      };
    } catch (err: any) {
      console.error("[LiveCall] startCall failed:", err);
      const msg = err?.name === "NotAllowedError"
        ? "Microphone access denied — allow mic in browser settings"
        : err?.message || "Failed to start call";
      setStatusText(msg);
      setCallState("idle");
      await endCall();
    }
  }, [callState, wsUrl, ensureAudioContext, flushPlayback, scheduleOutputAudio, endCall]);

  /* ── Auto-cleanup on unmount ── */
  useEffect(() => {
    return () => {
      endCall();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="live-call-plugin">
      {/* Status indicator — lightweight CSS badge */}
      <div className={`live-call-status live-call-status--${callState}`}>
        <span className="live-call-dot" />
        <span className="live-call-label">
          {callState === "idle" && "Voice Call"}
          {callState === "connecting" && (statusText || "Connecting…")}
          {callState === "active" && (statusText || "On Call")}
          {callState === "ending" && "Ending…"}
        </span>
      </div>

      {/* Controls */}
      <div className="live-call-controls">
        {callState === "idle" ? (
          <button className="live-call-btn live-call-btn--start" onClick={startCall}>
            <Phone size={16} />
            <span>Start Call</span>
          </button>
        ) : (
          <>
            {/* Mute toggle */}
            <button
              className={`live-call-btn live-call-btn--mute ${micMuted ? "live-call-btn--muted" : ""}`}
              onClick={() => setMicMuted((m) => !m)}
              disabled={callState !== "active"}
            >
              {micMuted ? <MicOff size={16} /> : <Mic size={16} />}
            </button>

            {/* End call */}
            <button className="live-call-btn live-call-btn--end" onClick={endCall}>
              <PhoneOff size={16} />
              <span>End</span>
            </button>

            {/* Connecting spinner */}
            {callState === "connecting" && (
              <Loader2 size={16} className="live-call-spinner" />
            )}

            {/* Sound indicator while active */}
            {callState === "active" && (
              <Volume2 size={16} className="live-call-volume" />
            )}
          </>
        )}
      </div>
    </div>
  );
}
